import React from 'react';
import styled, { keyframes } from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Placeholder from '@component/common/placeholder';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { v4 as uuid4 } from 'uuid';
import Chip from '@design-system/chip';
import IconBlock from '@component/common/block/icon-block';
import Quote from '@design-system/quote';
import Canvas from '@design-system/canvas';
import { IClozeExplanation } from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

interface IProps {
  title: string;
  description: string;
  vocabulary: IClozeExplanation;
  current: number;
  total: number;
  revealed: boolean;
  handleReveal: () => void;
}

const pointerFloat = keyframes`
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-2px);
    }
`;
const MeaningButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: ${size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT};
  cursor: pointer;
`;
const PointerContainer = styled.span`
  display: flex;
  animation: ${pointerFloat} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const KanjiItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;
const VocabularyContent: React.FC<IProps> = ({
  title,
  description,
  vocabulary,
  current,
  total,
  revealed,
  handleReveal,
}) => {
  const kanjis = vocabulary.kanji ?? [];
  const KanjiItems = kanjis.map((item) => (
    <KanjiItemContainer key={uuid4()}>
      <Gap height='5px' />
      <Quote width='100%'>
        <TextBlock
          text={`${item.character} ${item.meaning.join(', ')}`}
          design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
        />
        <TextBlock
          text={[
            item.onyomi?.length ? `· 음독 ${item.onyomi.join(' · ')}` : '',
            item.kunyomi?.length ? `· 훈독 ${item.kunyomi.join(' · ')}` : '',
          ]
            .filter(Boolean)
            .join('\n')}
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
      </Quote>
    </KanjiItemContainer>
  ));
  const reading = vocabulary.pronunciation?.[0];
  return (
    <Wrapper>
      <TextBlock
        text={title}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      {/*<Gap height={size.MARGIN_1} />*/}
      <TextBlock
        text={description}
        design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <Chip
        text={`어휘 ${current} / ${total}`}
        width='75px'
        size='small'
        theme={ETheme.BROWN}
        kind='outlined'
        usage='static'
      />
      <Gap height={size.MARGIN_2} />
      <Canvas
        theme={ETheme.PAPER}
        shadow
        shape='round'
        width='100%'
        padding={`${size.MARGIN_3} ${size.MARGIN_1}`}
        alignX='center'>
        <TextBlock
          text={vocabulary.keyword}
          reserveAnnotationSpace={!!reading}
          annotation={reading ? { reading, color: revealed ? undefined : 'transparent' } : undefined}
          design={{ text: { size: 'big', color: color.BLACK_2, bold: true } }}
        />
        <Gap height={size.MARGIN_2} />
        <Placeholder
          visible={revealed}
          placeholder={
            <MeaningButtonContainer onClick={handleReveal}>
              <PointerContainer>
                <IconBlock icon='touch-app' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
              </PointerContainer>
              <TextBlock text='뜻 보기' design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
            </MeaningButtonContainer>
          }>
          <TextBlock
            text={vocabulary.meaning.join(', ')}
            design={{ text: { size: 'large', color: color.BROWN_1 }, box: { alignX: 'center' } }}
          />
        </Placeholder>
      </Canvas>
      {revealed && (
        <>
          <Gap height={size.MARGIN_2} />
          {kanjis.length > 0 && (
            <>
              <TextBlock
                text='한자'
                design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
              />
              {KanjiItems}
            </>
          )}
          {!!vocabulary.example?.length && (
            <>
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text='예문'
                design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
              />
              {vocabulary.example.map((example, index) => (
                <React.Fragment key={index}>
                  <Gap height='5px' />
                  <Quote width='100%'>
                    <TextBlock
                      text={example.sentence}
                      design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
                    />
                    <TextBlock
                      text={example.translation}
                      design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
                    />
                  </Quote>
                </React.Fragment>
              ))}
            </>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default VocabularyContent;
